import React, { memo, useCallback, useMemo, useState } from 'react';

import AttachDocuments from '@components/common/AttachDocuments';
import Modal from '@components/common/Modal';

import { useMutateMavenirConfigCreate } from '@hooks/dataSources/useMutateMavenirConfigCreate';
import { useMutateMavenirConfigUpload } from '@hooks/dataSources/useMutateMavenirConfigUpload';

import { IMavenirConfigToCreate } from '@interfaces/cmModule/dataSources';
import { IMavenirConfigCreateDTO } from '@interfaces/cmModule/dto/dataSourceDTO';

import { MODAL_WIDTH_480 } from '@constants/modals';
import {
	acceptedFileTypes,
	additionalText,
	fileErrors,
	MAX_FILES,
	supportsText,
} from './constants';

import CheckConfigModal from './CheckConfigModal';

interface IAttachMavenirConfig {
	onClose: () => void;
	setIsDirtyForm: (isDirty: boolean) => void;
}

function prepareConfigsToCreate(arr: IMavenirConfigToCreate[]): IMavenirConfigCreateDTO[] {
	const grouped = arr.reduce((acc: Record<string, IMavenirConfigCreateDTO>, curr) => {
		if (!acc[curr.importId]) {
			acc[curr.importId] = {
				fileNamesUpdateIfExist: [curr.fileName],
				importId: curr.importId,
				updateAllExist: false,
			};
		} else {
			acc[curr.importId].fileNamesUpdateIfExist.push(curr.fileName);
		}

		return acc;
	}, {});

	return Object.values(grouped);
}

const AttachMavenirConfig = ({
	onClose,
	setIsDirtyForm,
}: IAttachMavenirConfig) => {
	const [configsToCreate, setConfigsToCreate] = useState<IMavenirConfigToCreate[]>([]);

	const {
		createUploadUrlAttachment,
		progress,
	} = useMutateMavenirConfigUpload({
		setIsDirtyForm,
		setConfigsToCreate,
	});

	const { createConfig, isLoadingCreateMavenirConfig } = useMutateMavenirConfigCreate(onClose);

	const clearUploadedFiles = useCallback(() => setConfigsToCreate([]), [setConfigsToCreate]);

	const onUploadClickHandler = useCallback(() => {
		if (!configsToCreate.length) return;

		const configs = prepareConfigsToCreate(configsToCreate);

		setIsDirtyForm(false);
		createConfig({ dtos: configs });
	}, [configsToCreate, createConfig, setIsDirtyForm]);

	const existingFiles = useMemo(() => configsToCreate.filter((file) => file.isSkip), [configsToCreate]);

	const uploadedFiles = useMemo(() => {
		return configsToCreate.filter((file) => !file.isSkip).map((file) => file.file);
	}, [configsToCreate]);

	const filteredFiles = useMemo(() => [...Array.from(new Set(uploadedFiles))], [uploadedFiles]);

	return (
		<>
			<Modal
				isCloseButtonHidden
				isOpen={!!existingFiles.length}
				onClose={clearUploadedFiles}
				width={MODAL_WIDTH_480}
			>
				<CheckConfigModal
					existingFiles={existingFiles}
					setConfigsToCreate={setConfigsToCreate}
				/>
			</Modal>
			<AttachDocuments
				acceptedFileTypes={acceptedFileTypes}
				additionalText={additionalText}
				createUploadUrlAttachment={createUploadUrlAttachment}
				fileErrors={fileErrors}
				fileRejectionErrorHandler={clearUploadedFiles}
				filteredFiles={filteredFiles}
				isUploading={isLoadingCreateMavenirConfig}
				maxBytes={Infinity}
				maxFiles={MAX_FILES}
				nextButtonText='Proceed'
				onDropAcceptedHandler={clearUploadedFiles}
				onUploadClickHandler={onUploadClickHandler}
				progress={progress}
				supportsText={supportsText}
				toggleUploadFilesModal={onClose}
				uploadedFilesLength={configsToCreate.length}
				uploadedText='The file will be parsed using the character set UTF-8'
			/>
		</>

	);
};

export default memo(AttachMavenirConfig);
